// Lib
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

// Utils
import { isFullAccess } from '../../../../../../../common/permissions/permissionUtil';
import { getElementId, getTitle } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import {
    isPlatformPhoneOrMobileMode,
    isPlatformModernMobileApp,
} from '../../../../../../platform/utils/platformDetailsUtils';
import { validateBoardTitle } from '../../boardSharingValidationUtils';

// Selectors
import {
    currentBoardEditPermissionIdSelector,
    currentBoardHasPublicEditPermissionsSelector,
    publicEditAncestorBoardSelector,
} from '../../../../../../utils/permissions/elementPermissionsSelector';
import { currentBoardEditorsSelector } from './boardEditorPanelSelector';
import { getPlatformDetailsSelector } from '../../../../../../platform/platformSelector';
import { getCurrentBoard } from '../../../../../../reducers/currentBoardId/currentBoardIdSelector';

// Actions
import { disablePublicEditing, enablePublicEditing } from '../publish/publishBoardActions';
import { deselectAllElements } from '../../../../../../element/selection/selectionActions';
import { resetBoardPermissionLink } from '../../../../../sharing/boardSharingActions';

// Components
import SettingsRow from '../../../../../../user/account/accountModal/components/SettingsRow';
import ToggleInput from '../../../../../../components/form/fields/ToggleInput';
import BoardNotificationToggleSettingsRow from './BoardNotificationToggleSettingsRow';
import BoardEditorAddSection from './BoardEditorAddSection';
import PublicBoardURLField from '../../../PublicBoardURLField';
import ResetPermissionsLinkWarning from './ResetPermissionsLinkWarning';
import TooltipSource from '../../../../../../components/tooltips/TooltipSource';
import ToggleContents from '../../../../../../components/form/fields/ToggleContents';
import BoardEditorsSection from './BoardEditorsSection';

// Constants
import { SHARING_HELP_URL } from '../../../../../../../common/utils/urlConstants';
import { TooltipPositions } from '../../../../../../components/tooltips/tooltipConstants';
import { TIMES } from '../../../../../../../common/utils/timeUtil';

// Styles
import './BoardEditorPanel.scss';

const BoardEditorPanel = (props) => {
    const { currentBoardId, userPermission, setErrorToast } = props;

    const dispatch = useDispatch();

    const currentBoard = useSelector(getCurrentBoard);
    const currentBoardEditors = useSelector(currentBoardEditorsSelector);
    const hasPublicEditPermissions = useSelector(currentBoardHasPublicEditPermissionsSelector);
    const publicEditAncestorBoard = useSelector(publicEditAncestorBoardSelector);
    const currentBoardEditPermissionsId = useSelector(currentBoardEditPermissionIdSelector);
    const platformDetails = useSelector(getPlatformDetailsSelector);

    useEffect(() => {
        dispatch(deselectAllElements());
    }, []);

    const [showResetWarning, setShowResetWarning] = useState(false);

    const handlingToggleRef = useRef(false);

    const hasPermissionToChangePermissions = isFullAccess(userPermission);

    const publicEditPermissionsAreEditable =
        !hasPublicEditPermissions || getElementId(publicEditAncestorBoard) === currentBoardId;

    const isPhoneOrMobileAppMode = isPlatformPhoneOrMobileMode(platformDetails);

    /**
     * Validates board title and shows error toast if needed
     */
    const validateAndShowErrorToast = () => {
        const boardTitle = getTitle(currentBoard);
        const errorMessage = validateBoardTitle(boardTitle, isPhoneOrMobileAppMode);

        if (errorMessage) {
            setErrorToast(errorMessage);
            return false;
        }

        return true;
    };

    /**
     * Public edit permissions toggling.
     */
    const onPublicEditToggle = async (event) => {
        if (handlingToggleRef.current) return;

        const nowEnabled = event.target.checked;

        // Validate board title before enabling public edit permissions
        if (nowEnabled) {
            const validationPassed = validateAndShowErrorToast();
            if (!validationPassed) {
                return; // Don't proceed with enabling
            }
        }

        handlingToggleRef.current = true;

        try {
            await dispatch(
                nowEnabled
                    ? enablePublicEditing({ boardId: currentBoardId })
                    : disablePublicEditing({ boardId: currentBoardId }),
            );
        } catch (err) {
            setErrorToast('Failed to update invite editors link');
        } finally {
            handlingToggleRef.current = false;
        }
    };

    /**
     * Reset public edit permissions link handler.
     */
    const onResetPermissionLink = () => {
        dispatch(resetBoardPermissionLink({ boardId: currentBoardId, permissionId: currentBoardEditPermissionsId }));
        setShowResetWarning(false);
    };

    return (
        <div className="BoardEditorPanel ShareModalColumn">
            <h1>Add editors</h1>

            <p className="explanation">
                Anyone you invite can edit this board with a free Milanote account.&nbsp;
                {!isPlatformModernMobileApp(platformDetails) && (
                    <a href={SHARING_HELP_URL} target="_blank" rel="noopener noreferrer">
                        Learn more
                    </a>
                )}
            </p>

            {hasPermissionToChangePermissions ? (
                <>
                    <div className="strong">
                        <label>
                            <span className="inline-error-message">Invite editors via email</span>
                            <BoardEditorAddSection
                                isPhoneOrMobileAppMode={isPhoneOrMobileAppMode}
                                onValidateBeforeAdd={validateAndShowErrorToast}
                                onClearError={() => setErrorToast(null)}
                            />
                        </label>
                    </div>

                    <div className="BoardEditorsPublicEditSection">
                        <SettingsRow className="strong" disableLabelToggle label="Invite editors with a link">
                            <TooltipSource
                                className="share-link-toggle-tooltip small-text"
                                enabled={!publicEditPermissionsAreEditable}
                                triggerOnClick
                                position={TooltipPositions.BOTTOM}
                                duration={TIMES.SECOND * 3}
                                offset={-8}
                                tooltipText={`Change this setting in: ${getTitle(publicEditAncestorBoard)}`}
                            >
                                <ToggleInput
                                    disabled={!publicEditPermissionsAreEditable}
                                    active={hasPublicEditPermissions}
                                    onChange={onPublicEditToggle}
                                />
                            </TooltipSource>
                        </SettingsRow>
                        <ToggleContents active>
                            <div
                                className={classNames('setting-contents', {
                                    disabled: !(hasPublicEditPermissions && !!currentBoardEditPermissionsId),
                                })}
                            >
                                <PublicBoardURLField
                                    currentBoardId={currentBoardId}
                                    permissionsId={currentBoardEditPermissionsId}
                                />
                            </div>
                        </ToggleContents>
                    </div>
                </>
            ) : (
                <>
                    <div className="BoardEditorsPublicEditSection">
                        <div className="SettingsRow strong">
                            <p className="label">Invite editors with a link</p>
                        </div>
                        <div className="setting-contents">
                            <PublicBoardURLField
                                currentBoardId={currentBoardId}
                                permissionsId={currentBoardEditPermissionsId}
                            />
                        </div>
                    </div>
                </>
            )}

            <BoardEditorsSection
                transitioning={handlingToggleRef.current}
                currentBoardEditors={currentBoardEditors}
                isPhoneOrMobileAppMode={isPhoneOrMobileAppMode}
            />

            <BoardNotificationToggleSettingsRow
                transitioning={handlingToggleRef.current}
                currentBoardId={currentBoardId}
            />

            {showResetWarning && (
                <ResetPermissionsLinkWarning
                    onConfirm={onResetPermissionLink}
                    onCancel={() => setShowResetWarning(false)}
                />
            )}
        </div>
    );
};

BoardEditorPanel.propTypes = {
    currentBoardId: PropTypes.string,
    currentBoardEditors: PropTypes.array,
    userPermission: PropTypes.number,
    setErrorToast: PropTypes.func,
};

export default BoardEditorPanel;
